import { useEffect, useState } from 'react';
import { adminPerformanceApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Database, Lightning, ArrowsClockwise, HardDrives, Warning, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AdminPerformance = () => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminPerformanceApi.get();
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rebuild = async () => {
    if (!window.confirm(language === 'fr'
      ? 'Reconstruire les index sur toutes les collections critiques ? Cette opération est idempotente et ne supprime aucune donnée.'
      : 'Rebuild indexes on all critical collections? Idempotent — no data is deleted.')) return;
    setRebuilding(true);
    try {
      const { data: res } = await adminPerformanceApi.rebuildIndexes();
      const s = res.summary;
      toast.success(`Index OK — ${s.created} créés/rafraîchis, ${s.skipped} déjà présents, ${s.failed} échecs`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec');
    } finally {
      setRebuilding(false);
    }
  };

  if (loading || !data) {
    return <div className="p-8 font-manrope text-[#64748B]">{language === 'fr' ? 'Chargement…' : 'Loading…'}</div>;
  }

  const t = data.totals;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-[#0F172A] flex items-center gap-3">
            <ChartBar size={32} weight="duotone" className="text-[#0052FF]" />
            {language === 'fr' ? 'Santé Performance' : 'Performance Health'}
          </h1>
          <p className="font-manrope text-sm text-[#64748B] mt-1">
            {language === 'fr'
              ? 'Vision technique de la base de données et des index.'
              : 'Technical view of database and indexes.'}
          </p>
        </div>
        <Button
          onClick={rebuild}
          disabled={rebuilding}
          className="bg-[#0052FF] hover:bg-[#0040CC]"
          data-testid="rebuild-indexes-btn"
        >
          <ArrowsClockwise size={16} className="mr-2" />
          {rebuilding
            ? (language === 'fr' ? 'Reconstruction…' : 'Rebuilding…')
            : (language === 'fr' ? 'Reconstruire les index' : 'Rebuild indexes')}
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="perf-totals">
        {[
          { icon: Database, label: language === 'fr' ? 'Documents' : 'Documents', value: t.objects.toLocaleString('fr-FR'), color: 'blue' },
          { icon: HardDrives, label: language === 'fr' ? 'Données' : 'Data', value: t.data_size_human, color: 'emerald' },
          { icon: Lightning, label: language === 'fr' ? 'Index' : 'Indexes', value: `${t.indexes} (${t.index_size_human})`, color: 'indigo' },
          { icon: Warning, label: language === 'fr' ? 'Ops lentes' : 'Slow ops', value: data.slow_ops.length, color: data.slow_ops.length > 0 ? 'red' : 'gray' },
        ].map((card, i) => {
          const Icon = card.icon;
          const colorMap = {
            blue: 'bg-blue-50 text-blue-600',
            emerald: 'bg-emerald-50 text-emerald-600',
            indigo: 'bg-indigo-50 text-indigo-600',
            red: 'bg-red-50 text-red-600',
            gray: 'bg-gray-50 text-gray-500',
          };
          return (
            <Card key={i} className="border border-[#E2E8F0] shadow-none">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-manrope text-xs text-[#64748B] uppercase tracking-wide">{card.label}</p>
                    <p className="font-outfit text-2xl font-bold text-[#0F172A] mt-1">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${colorMap[card.color]}`}>
                    <Icon size={22} weight="duotone" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Collections */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-lg">
            {language === 'fr' ? 'Collections principales' : 'Main collections'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead>{language === 'fr' ? 'Collection' : 'Collection'}</TableHead>
                <TableHead className="text-right">{language === 'fr' ? 'Documents' : 'Documents'}</TableHead>
                <TableHead className="text-right">{language === 'fr' ? 'Données' : 'Data'}</TableHead>
                <TableHead className="text-right">{language === 'fr' ? 'Stockage' : 'Storage'}</TableHead>
                <TableHead>{language === 'fr' ? 'Index' : 'Indexes'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.collections.map((c) => (
                <TableRow key={c.name} className="border-[#E2E8F0]" data-testid={`coll-row-${c.name}`}>
                  <TableCell className="font-mono text-xs">{c.name}</TableCell>
                  <TableCell className="text-right font-semibold">{c.count.toLocaleString('fr-FR')}</TableCell>
                  <TableCell className="text-right text-[#64748B]">{c.size_human}</TableCell>
                  <TableCell className="text-right text-[#64748B]">{c.storage_human}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {c.indexes.length === 0 ? (
                        <span className="text-xs text-[#94A3B8]">—</span>
                      ) : c.indexes.map((idx) => (
                        <span key={idx.name} className="inline-flex items-center text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-mono">
                          {idx.name} <span className="ml-1 text-indigo-400">{idx.size_human}</span>
                        </span>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Slow ops (if any) */}
      {data.slow_ops.length > 0 && (
        <Card className="border border-red-200 bg-red-50/30 shadow-none" data-testid="slow-ops-card">
          <CardHeader>
            <CardTitle className="font-outfit text-lg text-red-700 flex items-center gap-2">
              <Warning size={20} weight="duotone" />
              {language === 'fr' ? 'Opérations lentes en cours' : 'Slow operations in progress'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 font-mono text-xs">
              {data.slow_ops.map((op, i) => (
                <li key={i} className="border-l-2 border-red-400 pl-3">
                  <strong>{op.op}</strong> on <span className="text-indigo-700">{op.ns}</span> — {op.secs_running}s
                  <div className="text-[#64748B] truncate">{op.command_summary}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <p className="text-xs font-manrope text-[#94A3B8]">
        {language === 'fr' ? 'Généré le' : 'Generated at'} {new Date(data.generated_at).toLocaleString('fr-FR')}
      </p>
    </div>
  );
};

export default AdminPerformance;
